import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import surveyService from '../../services/surveyService';
import NotificationModal from '../reservations/components/NotificationModal';

const SurveyTemplatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    description: '',
    sortOrder: 0,
    isActive: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      console.log('📋 Carregando templates...');
      const data = await surveyService.getSurveyTemplates();
      console.log('📋 Templates carregados:', data);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Erro ao carregar templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      console.log('💾 Salvando pergunta:', formData);
      
      if (editingTemplate) {
        const result = await surveyService.updateSurveyTemplate(editingTemplate.id, formData);
        console.log('✅ Pergunta atualizada:', result);
        setNotificationData({
          type: 'success',
          title: 'Pergunta Atualizada!',
          message: 'A pergunta foi atualizada com sucesso.'
        });
      } else {
        const result = await surveyService.createSurveyTemplate(formData);
        console.log('✅ Pergunta criada:', result);
        setNotificationData({
          type: 'success',
          title: 'Pergunta Criada!',
          message: 'A pergunta foi criada com sucesso.'
        });
      }
      
      await loadTemplates();
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ question: '', description: '', sortOrder: 0, isActive: true });
      setShowNotification(true);
    } catch (error) {
      console.error('❌ Erro ao salvar template:', error);
      console.error('❌ Resposta do erro:', error.response?.data);
      setNotificationData({
        type: 'error',
        title: 'Erro ao Salvar',
        message: error.response?.data?.error || 'Erro ao salvar pergunta. Tente novamente.'
      });
      setShowNotification(true);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      question: template.question,
      description: template.description || '',
      sortOrder: template.sortOrder,
      isActive: template.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await surveyService.deleteSurveyTemplate(editingTemplate.id);
      await loadTemplates();
      setShowDeleteModal(false);
      setEditingTemplate(null);
      setNotificationData({
        type: 'success',
        title: 'Pergunta Excluída!',
        message: 'A pergunta foi excluída com sucesso.'
      });
      setShowNotification(true);
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      setNotificationData({
        type: 'error',
        title: 'Erro ao Excluir',
        message: error.response?.data?.error || 'Erro ao excluir pergunta. Tente novamente.'
      });
      setShowNotification(true);
    }
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setFormData({ question: '', description: '', sortOrder: templates.length, isActive: true });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onCollapseChange={setIsSidebarCollapsed}
      />
      
      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <div className="p-6 md:p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Perguntas de Avaliação</h1>
                <p className="text-muted-foreground mt-1">
                  Configure as perguntas que serão exibidas na avaliação de satisfação
                </p>
              </div>
              <Button onClick={handleNew} iconName="Plus">
                Nova Pergunta
              </Button>
            </div>

            {/* Info Card */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={24} className="text-primary flex-shrink-0 mt-1" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Como funciona:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>As perguntas serão exibidas na página pública de avaliação</li>
                    <li>O hóspede responderá de 1 a 5 estrelas para cada pergunta</li>
                    <li>Avaliação geral obrigatória sempre aparecerá</li>
                    <li>Perguntas inativas não serão exibidas</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Templates List */}
            <div className="bg-card border border-border rounded-lg">
              {templates.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Nenhuma pergunta configurada</p>
                  <Button onClick={handleNew} variant="outline" iconName="Plus">
                    Criar Primeira Pergunta
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {templates
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((template) => (
                    <div key={template.id} className="p-6 hover:bg-muted/20 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">
                              #{template.sortOrder + 1}
                            </span>
                            {!template.isActive && (
                              <span className="bg-muted text-muted-foreground px-2 py-1 rounded text-xs">
                                Inativa
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-foreground mb-1">{template.question}</h3>
                          {template.description && (
                            <p className="text-sm text-muted-foreground">{template.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(template)}
                            title="Editar"
                          >
                            <Icon name="Edit" size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingTemplate(template);
                              setShowDeleteModal(true);
                            }}
                            title="Excluir"
                          >
                            <Icon name="Trash2" size={18} className="text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
      </main>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                {editingTemplate ? 'Editar Pergunta' : 'Nova Pergunta'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pergunta *
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                  placeholder="Ex: Como avalia a limpeza do quarto?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descrição (Opcional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                  rows={3}
                  placeholder="Descrição adicional da pergunta"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ordem *
                  </label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <select
                    value={formData.isActive ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground"
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTemplate(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingTemplate ? 'Salvar Alterações' : 'Criar Pergunta'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        type={notificationData?.type || 'success'}
        title={notificationData?.title || ''}
        message={notificationData?.message || ''}
        onClose={() => setShowNotification(false)}
      />

      {/* Delete Confirmation */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Confirmar Exclusão
            </h3>
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir esta pergunta? Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setEditingTemplate(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
              >
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyTemplatesPage;

